import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import type { ReactElement } from 'react'

import { borderRadius, colorTokens } from '../../foundations/index.ts'
import { cssPropsPerScreenVariantPerTextVariant } from '../../foundations/text/css-props.ts'
import { buttonDimensionsPerButtonSize, type ButtonSize } from '../button/sizes.ts'

export type SkeletonVariant = 'text' | 'rectangular' | 'rounded' | 'circular'

export type SkeletonProps = {
  variant?: SkeletonVariant
  width?: number | string
  height?: number | string
  className?: string
}

function SkeletonRoot({ variant = 'text', width, height, className }: SkeletonProps): ReactElement {
  return (
    <SkeletonShimmer
      width={width ?? (variant === 'text' ? '100%' : 'auto')}
      height={height ?? (variant === 'text' ? '1em' : 'auto')}
      radius={radiusFor(variant)}
      display="block"
      className={className}
    />
  )
}

export type SkeletonTextSize = 'body1' | 'body2' | 'caption'

export type SkeletonTextProps = {
  size?: SkeletonTextSize
  width?: number | string
  className?: string
}

function SkeletonText({ size = 'body1', width = '100%', className }: SkeletonTextProps): ReactElement {
  const lineHeight = cssPropsPerScreenVariantPerTextVariant[size].backoffice.lineHeight
  return (
    <SkeletonShimmer
      width={width}
      height={lineHeight}
      radius={borderRadius[10]}
      display="inline-block"
      className={className}
    />
  )
}

export type SkeletonBadgeProps = {
  width?: number | string
  className?: string
}

const BADGE_HEIGHT = '28px'
const PILL_RADIUS = '9999px'

function SkeletonBadge({ width = 140, className }: SkeletonBadgeProps): ReactElement {
  return (
    <SkeletonShimmer
      width={width}
      height={BADGE_HEIGHT}
      radius={PILL_RADIUS}
      display="inline-block"
      className={className}
    />
  )
}

export type SkeletonButtonProps = {
  size?: ButtonSize
  width?: number | string
  className?: string
}

const buttonDefaultWidthPerSize: { [size in ButtonSize]: number } = {
  small: 140,
  medium: 160,
  large: 180,
}

function SkeletonButton({ size = 'medium', width, className }: SkeletonButtonProps): ReactElement {
  return (
    <SkeletonShimmer
      width={width ?? buttonDefaultWidthPerSize[size]}
      height={buttonDimensionsPerButtonSize[size].height}
      radius={borderRadius[20]}
      display="inline-block"
      className={className}
    />
  )
}

export type SkeletonAvatarProps = {
  size?: number
  className?: string
}

function SkeletonAvatar({ size = 32, className }: SkeletonAvatarProps): ReactElement {
  return <SkeletonShimmer width={size} height={size} radius="50%" display="inline-block" className={className} />
}

export type SkeletonBlockRadius = 'sharp' | 'rounded' | 'circular'

export type SkeletonBlockProps = {
  width?: number | string
  height?: number | string
  radius?: SkeletonBlockRadius
  className?: string
}

const radiusForBlock: { [radius in SkeletonBlockRadius]: string } = {
  sharp: '0',
  rounded: borderRadius[20],
  circular: '50%',
}

function SkeletonBlock({
  width = '100%',
  height = '100%',
  radius = 'rounded',
  className,
}: SkeletonBlockProps): ReactElement {
  return (
    <SkeletonShimmer
      width={width}
      height={height}
      radius={radiusForBlock[radius]}
      display="inline-block"
      className={className}
    />
  )
}

export function Skeleton(props: SkeletonProps): ReactElement {
  return <SkeletonRoot {...props} />
}

Skeleton.Text = SkeletonText
Skeleton.Badge = SkeletonBadge
Skeleton.Button = SkeletonButton
Skeleton.Avatar = SkeletonAvatar
Skeleton.Block = SkeletonBlock

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`

const toCssLength = (value: number | string): string => (typeof value === 'number' ? `${value}px` : value)

const radiusFor = (variant: SkeletonVariant): string => {
  switch (variant) {
    case 'rounded':
      return borderRadius[20]
    case 'circular':
      return '50%'
    case 'text':
      return borderRadius[10]
    case 'rectangular':
      return '0'
  }
}

type SkeletonShimmerProps = {
  width: number | string
  height: number | string
  radius: string
  display: 'block' | 'inline-block'
  className?: string | undefined
}

function SkeletonShimmer({ width, height, radius, display, className }: SkeletonShimmerProps): ReactElement {
  return (
    <StyledShimmer
      role="presentation"
      aria-hidden
      className={className}
      shimmerWidth={width}
      shimmerHeight={height}
      shimmerRadius={radius}
      shimmerDisplay={display}
    />
  )
}

type StyledShimmerProps = {
  shimmerWidth: number | string
  shimmerHeight: number | string
  shimmerRadius: string
  shimmerDisplay: 'block' | 'inline-block'
}

const StyledShimmer = styled('span', {
  shouldForwardProp: (prop) =>
    prop !== 'shimmerWidth' && prop !== 'shimmerHeight' && prop !== 'shimmerRadius' && prop !== 'shimmerDisplay',
})<StyledShimmerProps>`
  display: ${({ shimmerDisplay }) => shimmerDisplay};
  vertical-align: middle;
  border-radius: ${({ shimmerRadius }) => shimmerRadius};
  width: ${({ shimmerWidth }) => toCssLength(shimmerWidth)};
  height: ${({ shimmerHeight }) => toCssLength(shimmerHeight)};
  background-color: ${colorTokens['color-bg-base-disable']};
  animation: ${pulse} 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`
